import { useState, useRef, type FC } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { TOAST } from "@/shared/constants/toastMessages";
import { LockKeyhole, RefreshCw } from "lucide-react";

import { SignatureStepLayout } from "@/shared/components/SignatureStepLayout";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { FieldError } from "@/shared/components/ui/FieldError";
import TPADocumentViewer from "@/shared/components/document-viewer/TPADocumentViewer";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { useDeceasedUserData } from "@/shared/hooks/useDeceasedUserData";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { sentryUtils } from "@/shared/utils/sentry";

import logoIcon from "@/shared/assets/logo/logo-icon.svg";

interface TPAModalProps extends FlowCustomComponentProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSigned: () => void;
}

const TPAHeader: FC = () => (
  <div className="relative h-16 w-[120px] shrink-0">
    <div className="absolute left-7 top-0 flex h-16 w-16 items-center justify-center rounded-full border-[1.778px] border-primary">
      <RefreshCw className="h-6 w-6 text-primary" />
    </div>
    <img src={logoIcon} alt="Elayne" className="absolute left-0 top-3 h-10 w-10 rounded-full" />
    <div className="absolute left-20 top-3 flex h-10 w-10 items-center justify-center rounded-full border-2 border-primary bg-background">
      <LockKeyhole className="h-5 w-5 text-secondary" />
    </div>
  </div>
);

export const TPAModal: FC<TPAModalProps> = ({ isOpen, onOpenChange, onSigned }) => {
  const deceasedUserData = useDeceasedUserData();
  const { authUser, user } = useAppContext();
  const queryClient = useQueryClient();
  const modalRef = useRef<HTMLDivElement>(null);

  const [fullName, setFullName] = useState("");
  const [esignConsent, setEsignConsent] = useState(false);
  const [errors, setErrors] = useState<{ fullName?: string; consent?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTPADocument, setShowTPADocument] = useState(false);

  const firstName = deceasedUserData?.personData?.first_name || "your loved one";
  const deceasedFullName = deceasedUserData.fullName || "your loved one";

  const validateName = (name: string) => {
    const words = name
      .trim()
      .split(/\s+/)
      .filter((w) => w.length > 0);
    return words.length >= 2;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
    if (errors.fullName) {
      setErrors((prev) => ({ ...prev, fullName: undefined }));
    }
  };

  const captureScreenshot = async (): Promise<string | null> => {
    try {
      const html2canvas = (await import("html2canvas")).default;
      const el = modalRef.current;
      if (!el) return null;
      await new Promise((r) => setTimeout(r, 100));
      await new Promise((r) => requestAnimationFrame(() => r(undefined)));
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(el, {
        backgroundColor: "#ffffff",
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: el.scrollWidth,
        height: el.scrollHeight,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      });
      return canvas.toDataURL("image/png");
    } catch (err) {
      sentryUtils.captureError(
        err,
        "Failed to capture TPA modal screenshot",
        { userId: user?.user_id, estateId: deceasedUserData?.personData?.estate_id },
        "warning",
      );
      return null;
    }
  };

  const handleSubmit = async () => {
    const newErrors: { fullName?: string; consent?: string } = {};
    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (!validateName(fullName)) {
      newErrors.fullName = "Please enter at least first and last name";
    }
    if (!esignConsent) {
      newErrors.consent = "You must agree to sign electronically";
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      const modalScreenshot = await captureScreenshot();

      const { error } = await supabase.functions.invoke("legal/tpa/sign", {
        body: {
          userEmail: authUser?.email || user?.email,
          userName: fullName,
          deceasedName: deceasedFullName,
          consentGiven: true,
          esignConsent,
          signatureType: "tpa",
          signatureStep: 1,
          totalSteps: 1,
          timestamp: new Date().toISOString(),
          processInBackground: true,
          estateId: deceasedUserData?.personData?.estate_id || undefined,
          modalScreenshot,
        },
      });

      if (error) throw new Error(error.message || "Failed to process authorization");

      if (authUser?.id) {
        await supabase
          .from("users")
          .update({ completed_authorization: true })
          .eq("auth_id", authUser.id);
      }

      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["userSettingsSupabase"] });
      queryClient.invalidateQueries({ queryKey: ["appContext"] });
      queryClient.invalidateQueries({ queryKey: ["documents"] });
      onSigned();
    } catch (err) {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
      sentryUtils.captureError(err, "TPA modal submission failed", {
        userId: user?.user_id,
        estateId: deceasedUserData?.personData?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const body = (
    <>
      <p className="text-sm text-secondary">
        This signature lets Elayne request records for {firstName}'s estate, so the calls, forms,
        and follow-ups with each institution happen for you. Review the{" "}
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-sm text-secondary underline"
          onClick={() => setShowTPADocument(true)}
        >
          Elayne Third-Party Authorization Agreement
        </Button>{" "}
        before signing.
      </p>
      <p className="text-sm text-secondary">
        Typing your name signs the agreement and confirms you've read it.
      </p>
      <label className="flex items-start gap-2 text-sm text-secondary cursor-pointer">
        <Checkbox
          checked={esignConsent}
          onCheckedChange={(checked) => {
            setEsignConsent(checked === true);
            if (errors.consent) {
              setErrors((prev) => ({ ...prev, consent: undefined }));
            }
          }}
          className="mt-0.5"
        />
        <span>I agree to sign and receive documents electronically.</span>
      </label>
      {errors.consent && <FieldError message={errors.consent} />}
    </>
  );

  return (
    <>
      <SignatureStepLayout
        open={isOpen}
        onOpenChange={onOpenChange}
        modalRef={modalRef}
        header={<TPAHeader />}
        stepLabel="Authorization"
        title="Let Elayne take work off your plate"
        body={body}
        fullName={fullName}
        onNameChange={handleNameChange}
        nameError={errors.fullName}
        isSubmitting={isSubmitting}
        isLastStep={true}
        onSubmit={handleSubmit}
        onClose={() => onOpenChange(false)}
      />

      <TPADocumentViewer isOpen={showTPADocument} onClose={() => setShowTPADocument(false)} />
    </>
  );
};
